// Application-wide constants

// HTTP Status Codes
export const HTTP_STATUS_OK = 200;
export const HTTP_STATUS_UNAUTHORIZED = 401;
export const HTTP_STATUS_NOT_FOUND = 404;
export const HTTP_STATUS_SERVER_ERROR = 500;

export const HTTP_STATUS = {
  OK: HTTP_STATUS_OK,
  UNAUTHORIZED: HTTP_STATUS_UNAUTHORIZED,
  NOT_FOUND: HTTP_STATUS_NOT_FOUND,
  SERVER_ERROR: HTTP_STATUS_SERVER_ERROR,
};

// Timeouts (milliseconds)
export const REQUEST_TIMEOUT_MS = 60000;
export const TOAST_DURATION_MS = 4000;
export const QUERY_STALE_TIME_MS = 60000;

export const TIMEOUTS = {
  REQUEST_TIMEOUT_MS,
  TOAST_DURATION_MS,
};

// Likert key truncation length (used to derive stable React keys from question text)
export const QUESTION_KEY_PREVIEW_LENGTH = 30;

// Chart / Radar constants
export const CHART = {
  RADAR_HEIGHT: 300,
  BAR_HEIGHT: 300,
  SCORE_MIN: 0,
  SCORE_MAX: 5,
  RADAR_ANGLE: 90,
  BAR_X_ANGLE: -15,
  BAR_X_HEIGHT: 80,
  BAR_RADIUS: [8, 8, 0, 0],
  TICK_STYLE: { fill: '#475569', fontSize: 12 },
  BAR_TICK_STYLE: { fill: '#475569', fontSize: 11 },
  POLAR_RADIUS_STYLE: { fill: '#475569' },
};

// UI / Layout
export const UI = {
  ASSESSMENT_TOTAL_STEPS: 5,
  LIKERT_SCALE: [1, 2, 3, 4, 5],
  STABILITY_ICON_SIZE: 48,
  ICON_SIZE_LARGE: 24,
  ICON_SIZE_MEDIUM: 20,
  ICON_SIZE_SMALL: 16,
};

// Lead Statuses
export const LEAD_STATUSES = [
  'New',
  'Contacted',
  'Consultation booked',
  'Proposal sent',
  'Converted',
  'Not interested',
];

// Stability levels
export const STABILITY_LEVELS = {
  LOW: 'Low Stability',
  MODERATE: 'Moderate Stability',
  HIGH: 'High Stability',
};

// Contact / Consultation details (single source of truth)
export const CONTACT = {
  COMPANY: 'Aurea Digital Solutions',
  EMAIL: 'aureadigitalteam@gmail.com',
  WHATSAPP_NUMBER: '+60 10-230 6534',
  WHATSAPP_RAW: '60102306534',
  WHATSAPP_URL: 'https://wa.me/60102306534?text=Hello%2C%20I%20completed%20the%20SMART%20Workflow%20Diagnostic%20and%20would%20like%20to%20discuss%20the%20results.',
  EMAIL_HREF: 'mailto:aureadigitalteam@gmail.com?subject=SMART%20Workflow%20Consultation%20Request',
};

/**
 * Build a WhatsApp deep link pre-filled with assessment context so a customer
 * can request the Professional Report (or any advisory engagement) with one tap.
 * Falls back to the generic CONTACT.WHATSAPP_URL when no assessment is supplied.
 */
export const buildWhatsAppConsultationUrl = ({
  organization, contactName, assessmentRef, awsaiScore, requestedService,
} = {}) => {
  if (!assessmentRef) return CONTACT.WHATSAPP_URL;
  const lines = [
    'Hello Aurea Digital Solutions,',
    '',
    'I have completed the AWSAI Workflow Diagnostic and would like to request the Professional Diagnostic Report.',
    '',
    `Organisation: ${organization || '—'}`,
    `Contact Person: ${contactName || '—'}`,
    `Assessment Reference: ${assessmentRef}`,
    awsaiScore != null ? `AWSAI Score: ${Number(awsaiScore).toFixed(2)} / 5.00` : null,
    `Requested Service: ${requestedService || 'Professional Diagnostic Report'}`,
    '',
    'Please share next steps for receiving the report and arranging a brief consultation.',
    '',
    'Thank you.',
  ].filter(Boolean);
  return `https://wa.me/${CONTACT.WHATSAPP_RAW}?text=${encodeURIComponent(lines.join('\n'))}`;
};

/**
 * Build a mailto: link pre-filled with the consultation request subject + body.
 */
export const buildEmailConsultationHref = ({
  organization, contactName, assessmentRef, awsaiScore, requestedService, preferredContact,
} = {}) => {
  if (!assessmentRef) return CONTACT.EMAIL_HREF;
  const subject = `AWSAI Consultation Request – ${assessmentRef}`;
  const bodyLines = [
    'Dear Aurea Digital Solutions Team,',
    '',
    'I have completed the AWSAI Workflow Diagnostic and would like to request the Professional Diagnostic Report and/or a brief consultation.',
    '',
    `Organisation: ${organization || '—'}`,
    `Contact Person: ${contactName || '—'}`,
    `Assessment Reference: ${assessmentRef}`,
    awsaiScore != null ? `AWSAI Score: ${Number(awsaiScore).toFixed(2)} / 5.00` : null,
    `Requested Service: ${requestedService || 'Professional Diagnostic Report'}`,
    `Preferred Contact Method: ${preferredContact || 'Email or WhatsApp'}`,
    '',
    'Please let me know the next steps.',
    '',
    'Kind regards,',
  ].filter(Boolean);
  return `mailto:${CONTACT.EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines.join('\n'))}`;
};

// Intellectual property / Branding
export const BRAND = {
  PRODUCT: 'AWSAI Workflow & AI Readiness Diagnostic',
  FRAMEWORK: 'AWSAI Framework',
  METHODOLOGY: 'SMART-Workflow Methodology',
  ORGANIZATION: 'Aurea Digital Solutions',
  IP_NOTICE: '© Aurea Digital Solutions · AWSAI Framework and SMART-Workflow Methodology · All Rights Reserved.',
};

// Z-index layering — explicit named constants so floating widgets don't compete for stacking.
export const Z_INDEX_FLOATING_WIDGET = 40;
export const Z_INDEX_STICKY_HEADER = 30;
export const Z_INDEX_TOAST = 9999;

// Lead status — value used to mark a fresh, untriaged submission
export const NEW_LEAD_STATUS = 'New';
